import { useQuery } from '@tanstack/react-query'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useState } from 'react'
import { GitBranch, Plus, Search, CheckCircle2, Clock, AlertTriangle } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  DataTable,
  EmptyState,
  ErrorState,
  FilterBar,
  FilterChip,
  Input,
  LoadingState,
  PageHeader,
  StatusBadge,
  type ColumnDef,
} from '@/components/ui'

interface ProcessInstance {
  id: string
  title: string
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled' | 'rejected'
  template_id: string
  template_name: string | null
  template_category: string | null
  started_by_user_id: string | null
  started_by_name: string | null
  current_step_title: string | null
  due_date: string | null
  completed_at: string | null
  created_at: string
  step_count: number
  completed_steps: number
}

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Ausstehend' },
  { value: 'in_progress', label: 'In Bearbeitung' },
  { value: 'completed', label: 'Abgeschlossen' },
  { value: 'cancelled', label: 'Abgebrochen' },
  { value: 'rejected', label: 'Abgelehnt' },
]

const STATUS_LABELS: Record<string, string> = {
  pending: 'Ausstehend',
  in_progress: 'In Bearbeitung',
  completed: 'Abgeschlossen',
  cancelled: 'Abgebrochen',
  rejected: 'Abgelehnt',
}

function fmt(iso: string | null): string {
  if (!iso) return '–'
  return new Date(iso).toLocaleDateString('de-DE')
}

function ProgressBar({ value, max }: { value: number; max: number }) {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1.5 bg-neutral-200 rounded-full overflow-hidden min-w-[60px]">
        <div
          className="h-full bg-brand-500 rounded-full transition-all"
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className="text-xs text-neutral-500 tabular-nums">{value}/{max}</span>
    </div>
  )
}

const columns: ColumnDef<ProcessInstance, unknown>[] = [
  {
    id: 'title',
    header: 'Prozess',
    accessorFn: (r) => r.title,
    cell: ({ row }) => (
      <div>
        <div className="font-medium text-neutral-900">{row.original.title}</div>
        {row.original.template_name && (
          <div className="text-xs text-neutral-500">{row.original.template_name}</div>
        )}
      </div>
    ),
  },
  {
    id: 'status',
    header: 'Status',
    accessorFn: (r) => r.status,
    cell: ({ row }) => <StatusBadge status={row.original.status} />,
  },
  {
    id: 'progress',
    header: 'Fortschritt',
    enableSorting: false,
    cell: ({ row }) => (
      <ProgressBar value={row.original.completed_steps} max={row.original.step_count} />
    ),
  },
  {
    id: 'current_step',
    header: 'Aktueller Schritt',
    accessorFn: (r) => r.current_step_title ?? '–',
    cell: ({ getValue }) => <span className="text-neutral-600 text-sm">{getValue() as string}</span>,
  },
  {
    id: 'started_by',
    header: 'Gestartet von',
    accessorFn: (r) => r.started_by_name ?? '–',
    cell: ({ getValue }) => <span className="text-neutral-600 text-sm">{getValue() as string}</span>,
  },
  {
    id: 'due_date',
    header: 'Fällig',
    accessorFn: (r) => r.due_date,
    cell: ({ row }) => {
      const d = row.original.due_date
      const overdue = d && new Date(d) < new Date() && row.original.status !== 'completed' && row.original.status !== 'cancelled'
      return (
        <span className={overdue ? 'text-red-600 font-medium' : 'text-neutral-600 text-sm'}>
          {fmt(d)}
        </span>
      )
    },
  },
  {
    id: 'created_at',
    header: 'Erstellt',
    accessorFn: (r) => r.created_at,
    cell: ({ getValue }) => <span className="text-neutral-500 text-sm">{fmt(getValue() as string)}</span>,
  },
]

export function ProcessListPage() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const [searchInput, setSearchInput] = useState(searchParams.get('q') ?? '')

  const statusFilter = searchParams.get('status') ?? ''
  const q = searchParams.get('q') ?? ''

  const { data, isLoading, error } = useQuery({
    queryKey: ['prozesse-instances', { status: statusFilter, q }],
    queryFn: () =>
      apiClient
        .get<ProcessInstance[]>('/prozesse/instances', {
          params: { status: statusFilter || undefined, q: q || undefined },
        })
        .then((r) => r.data),
  })

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams)
    if (value) next.set(key, value)
    else next.delete(key)
    setSearchParams(next)
  }

  const activeFilters = [
    statusFilter && { key: 'status', label: STATUS_LABELS[statusFilter] ?? statusFilter },
    q && { key: 'q', label: `Suche: ${q}` },
  ].filter(Boolean) as { key: string; label: string }[]

  const stats = {
    total: data?.length ?? 0,
    inProgress: data?.filter((r) => r.status === 'in_progress').length ?? 0,
    completed: data?.filter((r) => r.status === 'completed').length ?? 0,
    overdue: data?.filter((r) => r.due_date && new Date(r.due_date) < new Date() && r.status !== 'completed' && r.status !== 'cancelled').length ?? 0,
  }

  return (
    <div className="space-y-6 p-6">
      <PageHeader
        title="Prozesse"
        description="Strukturierte Abläufe und Freigabeprozesse"
        actions={
          <Button variant="primary" onClick={() => navigate('/prozesse/neu')}>
            <Plus className="w-4 h-4 mr-1.5" />
            Neuer Prozess
          </Button>
        }
      />

      {/* KPI summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[
          { label: 'Gesamt', value: stats.total, icon: GitBranch, color: 'text-neutral-600' },
          { label: 'In Bearbeitung', value: stats.inProgress, icon: Clock, color: 'text-blue-600' },
          { label: 'Abgeschlossen', value: stats.completed, icon: CheckCircle2, color: 'text-green-600' },
          { label: 'Überfällig', value: stats.overdue, icon: AlertTriangle, color: 'text-red-600' },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="bg-white rounded-lg border border-neutral-200 p-4 flex items-center gap-3">
            <Icon className={`w-5 h-5 ${color}`} />
            <div>
              <div className="text-2xl font-semibold text-neutral-900">{value}</div>
              <div className="text-xs text-neutral-500">{label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') setParam('q', searchInput) }}
            placeholder="Prozess oder Vorlage suchen…"
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {STATUS_OPTIONS.map(({ value, label }) => (
            <Button
              key={value}
              variant={statusFilter === value ? 'primary' : 'secondary'}
              size="sm"
              onClick={() => setParam('status', statusFilter === value ? '' : value)}
            >
              {label}
            </Button>
          ))}
        </div>
      </div>

      {activeFilters.length > 0 && (
        <FilterBar>
          {activeFilters.map(({ key, label }) => (
            <FilterChip
              key={key}
              active
              onRemove={() => {
                setParam(key, '')
                if (key === 'q') setSearchInput('')
              }}
            >
              {label}
            </FilterChip>
          ))}
        </FilterBar>
      )}

      {isLoading && <LoadingState label="Prozesse werden geladen…" />}
      {error && (
        <ErrorState
          title="Fehler beim Laden"
          description="Prozesse konnten nicht geladen werden."
          details={error instanceof Error ? error.message : String(error)}
        />
      )}
      {!isLoading && !error && data?.length === 0 && (
        <EmptyState
          icon={<GitBranch className="w-8 h-8" />}
          title="Keine Prozesse"
          description="Erstellen Sie den ersten strukturierten Ablauf."
          action={
            <Button variant="primary" onClick={() => navigate('/prozesse/neu')}>
              <Plus className="w-4 h-4 mr-1.5" />
              Neuer Prozess
            </Button>
          }
        />
      )}
      {!isLoading && !error && (data?.length ?? 0) > 0 && (
        <DataTable
          columns={columns}
          data={data ?? []}
          onRowClick={(row) => navigate(`/prozesse/${row.id}`)}
          searchable={false}
          pagination
        />
      )}
    </div>
  )
}
